import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { coursesAPI } from '../services/api';
import { scrollAndFlash } from '../lib/highlight';
import { SOLUTION_AREAS, getSolutionPlays, getSolutionAreaLabel, getSolutionPlayLabel } from '../constants/options';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Plus,
  Search,
  GraduationCap,
  Clock,
  Layers,
  BookOpen,
  RefreshCw,
  ExternalLink,
  Check,
  Filter,
  Tag,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { ColumnFilterPopover } from '../components/shared/ColumnFilterPopover';
import { ActiveFiltersBar } from '../components/shared/ActiveFiltersBar';
import { TablePagination, PAGE_SIZE } from '../components/shared/TablePagination';
import { format } from 'date-fns';

// ─── Local DB Course Card ───────────────────────────────────────────────────
const LocalCourseCard = ({ course }) => (
  <Card className="hover:shadow-md transition-shadow" data-testid={`course-card-${course.id}`}>
    <CardContent className="pt-5">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex flex-wrap items-center gap-1.5">
          {course.is_custom ? (
            <Badge variant="outline" className="text-xs">Custom</Badge>
          ) : (
            <Badge variant="secondary" className="text-xs font-mono">{course.pulse_id}</Badge>
          )}
          {course.solution_area && (
            <Badge className="text-xs bg-lime text-lime-foreground">
              {getSolutionAreaLabel(course.solution_area)}
            </Badge>
          )}
        </div>
      </div>
      <h3 className="font-semibold text-sm mb-1.5 leading-snug">{course.title}</h3>
      {course.description && (
        <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{course.description}</p>
      )}
      <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
        {course.duration_hours && (
          <div className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            {course.duration_hours}h
          </div>
        )}
        {course.solution_play && (
          <div className="flex items-center gap-1">
            <Layers className="w-3.5 h-3.5" />
            <span>{getSolutionPlayLabel(course.solution_area, course.solution_play)}</span>
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

// ─── Pulse Course Card ───────────────────────────────────────────────────────
const PulseCourseCard = ({ course, onAdd, isAdded, adding }) => (
  <Card
    className="hover:shadow-md transition-shadow border-l-4 border-l-blue-500"
    data-testid={`pulse-course-${course.pulse_id}`}
  >
    <CardContent className="pt-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5 mb-2">
            {course.pulse_id && (
              <Badge variant="secondary" className="text-xs font-mono">{course.pulse_id}</Badge>
            )}
            {course.level && (
              <Badge variant="outline" className={`text-xs ${getLevelColor(course.level)}`}>
                {course.level}
              </Badge>
            )}
            {course.technology && (
              <Badge className="text-xs bg-blue-100 text-blue-800">{course.technology}</Badge>
            )}
          </div>
          <h3 className="font-semibold text-sm mb-1.5 leading-snug">{course.title}</h3>
          {course.description && (
            <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{course.description}</p>
          )}
          <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
            {course.duration_hours && (
              <div className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {course.duration_hours}h
              </div>
            )}
            {course.url && (
              <a href={course.url} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1 text-blue-600 hover:underline"
                onClick={(e) => e.stopPropagation()}>
                <ExternalLink className="w-3.5 h-3.5" />
                View Course
              </a>
            )}
          </div>
        </div>
        <div className="flex-shrink-0">
          {isAdded ? (
            <Badge className="bg-green-100 text-green-800 gap-1 whitespace-nowrap">
              <Check className="w-3 h-3" /> Added
            </Badge>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onAdd(course)}
              disabled={adding}
              className="whitespace-nowrap"
              data-testid={`add-pulse-course-${course.pulse_id}`}
            >
              {adding ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <><Plus className="w-3.5 h-3.5 mr-1" />Add</>
              )}
            </Button>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);

const getLevelColor = (level) => {
  const l = level?.toLowerCase();
  if (l === 'beginner' || l === 'fundamental') return 'border-green-400 text-green-700';
  if (l === 'intermediate' || l === 'associate') return 'border-yellow-400 text-yellow-700';
  if (l === 'advanced' || l === 'expert' || l === 'expert') return 'border-red-400 text-red-700';
  return 'border-gray-300';
};

// ─── Main Page ───────────────────────────────────────────────────────────────
export const CoursesPage = () => {
  // Local courses state
  const [courses, setCourses] = useState([]);
  const [loadingLocal, setLoadingLocal] = useState(true);
  const [localPage, setLocalPage] = useState(1);
  const [localSearch, setLocalSearch] = useState('');
  const [searchParams] = useSearchParams();
  useEffect(() => { const q = searchParams.get('q'); if (q) setLocalSearch(q); }, [searchParams]);
  useEffect(() => {
    const hid = searchParams.get('highlight');
    if (!hid || !courses.length) return;
    const t = setTimeout(() => scrollAndFlash(`course-card-${hid}`), 400);
    return () => clearTimeout(t);
  }, [searchParams, courses]);
  const [localColFilters, setLocalColFilters] = useState({ solution_area: new Set() });
  const setLocalColFilter = (k, s) => setLocalColFilters(prev => ({ ...prev, [k]: s }));
  const [localTab, setLocalTab] = useState('all');

  // Pulse catalog state
  const [pulseCourses, setPulseCourses] = useState([]);
  const [pulseTotal, setPulseTotal] = useState(0);
  const [pulsePage, setPulsePage] = useState(1);
  const [loadingPulse, setLoadingPulse] = useState(false);
  const [pulseError, setPulseError] = useState(null);
  const [pulseLastUpdated, setPulseLastUpdated] = useState(null);
  const [pulseSearch, setPulseSearch] = useState('');
  const [pulseSearchInput, setPulseSearchInput] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [techFilter, setTechFilter] = useState('all');
  const [availableLevels, setAvailableLevels] = useState([]);
  const [availableTechs, setAvailableTechs] = useState([]);
  const [addingCourseId, setAddingCourseId] = useState(null);
  const [addedCourseIds, setAddedCourseIds] = useState(new Set());

  // Custom course dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '', description: '', duration_hours: '', solution_area: '', solution_play: '',
  });

  const searchTimer = useRef(null);

  // ── Local courses ────────────────────────────────────────────────────────
  const loadLocalCourses = useCallback(async () => {
    try {
      setLoadingLocal(true);
      const data = await coursesAPI.getAll();
      setCourses(data);
      // Track which Pulse IDs are already in local DB
      const existingIds = new Set(data.filter(c => c.pulse_id).map(c => c.pulse_id));
      setAddedCourseIds(existingIds);
    } catch {
      toast.error('Failed to load courses');
    } finally {
      setLoadingLocal(false);
    }
  }, []);

  useEffect(() => { loadLocalCourses(); }, [loadLocalCourses]);

  // ── Pulse catalog ────────────────────────────────────────────────────────
  const loadPulseCatalog = useCallback(async (page = 1, search = '', level = 'all', tech = 'all') => {
    try {
      setLoadingPulse(true);
      setPulseError(null);
      const data = await coursesAPI.getPulseCatalog({
        search: search || undefined,
        level: level !== 'all' ? level : undefined,
        technology: tech !== 'all' ? tech : undefined,
        page,
        limit: 24,
      });
      setPulseCourses(data.courses || []);
      setPulseTotal(data.total || 0);
      setPulsePage(page);
      if (data.last_updated) {
        setPulseLastUpdated(new Date(data.last_updated));
      }
    } catch (err) {
      setPulseError(err?.response?.data?.detail || 'Failed to load Pulse catalog');
    } finally {
      setLoadingPulse(false);
    }
  }, []);

  const loadPulseFilters = useCallback(async () => {
    try {
      const data = await coursesAPI.getPulseFilters();
      setAvailableLevels(data.levels || []);
      setAvailableTechs(data.technologies || []);
    } catch {
      // silently fail — filters are optional
    }
  }, []);

  const handleTabChange = (tab) => {
    if (tab === 'pulse' && pulseCourses.length === 0) {
      loadPulseCatalog(1, pulseSearch, levelFilter, techFilter);
      loadPulseFilters();
    }
  };

  // Debounced pulse search
  const handlePulseSearchInput = (val) => {
    setPulseSearchInput(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setPulseSearch(val);
      loadPulseCatalog(1, val, levelFilter, techFilter);
    }, 400);
  };

  const handleLevelFilter = (val) => {
    setLevelFilter(val);
    loadPulseCatalog(1, pulseSearch, val, techFilter);
  };

  const handleTechFilter = (val) => {
    setTechFilter(val);
    loadPulseCatalog(1, pulseSearch, levelFilter, val);
  };

  const handleRefreshPulse = async () => {
    try {
      setLoadingPulse(true);
      await coursesAPI.refreshPulseCache();
      await loadPulseCatalog(pulsePage, pulseSearch, levelFilter, techFilter);
      await loadPulseFilters();
      toast.success('Pulse catalog refreshed');
    } catch {
      toast.error('Failed to refresh catalog');
    } finally {
      setLoadingPulse(false);
    }
  };

  // ── Add from Pulse ────────────────────────────────────────────────────────
  const handleAddFromPulse = async (pulseCourse) => {
    try {
      setAddingCourseId(pulseCourse.pulse_id);
      await coursesAPI.create({
        pulse_id: pulseCourse.pulse_id,
        title: pulseCourse.title,
        description: pulseCourse.description,
        duration_hours: pulseCourse.duration_hours,
        solution_area: pulseCourse.solution_area || '',
        solution_play: pulseCourse.solution_play || '',
        is_custom: false,
      });
      toast.success(`"${pulseCourse.title}" added to My Courses`);
      setAddedCourseIds(prev => new Set([...prev, pulseCourse.pulse_id]));
      loadLocalCourses();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add course');
    } finally {
      setAddingCourseId(null);
    }
  };

  // ── Custom course create ──────────────────────────────────────────────────
  const handleCreateCustom = async (e) => {
    e.preventDefault();
    if (!formData.title) { toast.error('Title is required'); return; }
    try {
      await coursesAPI.createCustom({
        title: formData.title,
        description: formData.description,
        duration_hours: formData.duration_hours ? parseFloat(formData.duration_hours) : null,
        solution_area: formData.solution_area,
        solution_play: formData.solution_play,
      });
      toast.success('Custom course created');
      setIsDialogOpen(false);
      setFormData({ title: '', description: '', duration_hours: '', solution_area: '', solution_play: '' });
      loadLocalCourses();
    } catch {
      toast.error('Failed to create course');
    }
  };

  // ── Filter local courses ──────────────────────────────────────────────────
  const filteredLocalCourses = courses.filter(course => {
    if (localTab === 'catalog' && course.is_custom) return false;
    if (localTab === 'custom' && !course.is_custom) return false;
    if (localColFilters.solution_area.size && !localColFilters.solution_area.has(course.solution_area)) return false;
    if (!localSearch) return true;
    const s = localSearch.toLowerCase();
    return (
      course.title?.toLowerCase().includes(s) ||
      course.description?.toLowerCase().includes(s) ||
      getSolutionAreaLabel(course.solution_area)?.toLowerCase().includes(s)
    );
  });

  const localSolutionAreaOpts = [...new Set(courses.map(c => c.solution_area).filter(Boolean))].sort().map(v => ({ value: v, label: getSolutionAreaLabel(v) || v }));

  useEffect(() => { setLocalPage(1); }, [localSearch, localTab, localColFilters]);
  const pagedLocalCourses = filteredLocalCourses.slice((localPage - 1) * PAGE_SIZE, localPage * PAGE_SIZE);

  const availablePlays = getSolutionPlays(formData.solution_area);
  const PULSE_PAGE_SIZE = 24;
  const totalPages = Math.ceil(pulseTotal / PULSE_PAGE_SIZE);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="courses-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">Courses</h1>
          <p className="text-muted-foreground">Browse Pulse catalog and manage custom courses</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-lime text-lime-foreground hover:bg-lime/90" data-testid="add-course-button">
              <Plus className="w-4 h-4 mr-2" />
              Add Custom Course
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Custom Course</DialogTitle>
              <DialogDescription>Add a course not in the Pulse catalog</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateCustom} className="space-y-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Custom Training Course" data-testid="course-title-input" />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Course description..." rows={3} data-testid="course-description-input" />
              </div>
              <div className="space-y-2">
                <Label>Duration (hours)</Label>
                <Input type="number" min="0" step="0.5" value={formData.duration_hours}
                  onChange={(e) => setFormData({ ...formData, duration_hours: e.target.value })}
                  data-testid="course-duration-input" />
              </div>
              <div className="space-y-2">
                <Label>Solution Area</Label>
                <Select value={formData.solution_area} onValueChange={(v) => setFormData({ ...formData, solution_area: v, solution_play: '' })}>
                  <SelectTrigger data-testid="solution-area-select"><SelectValue placeholder="Select solution area..." /></SelectTrigger>
                  <SelectContent>
                    {SOLUTION_AREAS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {formData.solution_area && (
                <div className="space-y-2">
                  <Label>Solution Play</Label>
                  <Select value={formData.solution_play} onValueChange={(v) => setFormData({ ...formData, solution_play: v })}>
                    <SelectTrigger data-testid="solution-play-select"><SelectValue placeholder="Select solution play..." /></SelectTrigger>
                    <SelectContent>
                      {availablePlays.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-lime text-lime-foreground hover:bg-lime/90" data-testid="save-course-button">
                  Create Course
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="my-courses" onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="my-courses" data-testid="tab-my-courses">
            My Courses ({courses.length})
          </TabsTrigger>
          <TabsTrigger value="pulse" data-testid="tab-pulse-catalog">
            Pulse Catalog {pulseTotal > 0 && `(${pulseTotal})`}
          </TabsTrigger>
        </TabsList>

        {/* ── My Courses Tab ── */}
        <TabsContent value="my-courses" className="space-y-4 mt-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search courses..." value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)} className="pl-10"
                data-testid="search-local-courses" />
            </div>
            <div className="flex items-center gap-2 rounded-md border px-3 py-1.5 bg-card">
              <ColumnFilterPopover label="Solution Area" options={localSolutionAreaOpts} selected={localColFilters.solution_area} onChange={(s) => setLocalColFilter('solution_area', s)} testId="courses-filter-solution" />
            </div>
            <div className="flex gap-2">
              {['all', 'catalog', 'custom'].map(t => (
                <Button key={t} variant={localTab === t ? 'default' : 'outline'} size="sm"
                  onClick={() => setLocalTab(t)} className="capitalize">
                  {t === 'all' ? 'All' : t === 'catalog' ? 'From Pulse' : 'Custom'}
                </Button>
              ))}
            </div>
          </div>

          <ActiveFiltersBar
            filters={[{ key: 'solution_area', label: 'Solution Area', set: localColFilters.solution_area, options: localSolutionAreaOpts, onChange: (s) => setLocalColFilter('solution_area', s) }]}
            search={localSearch}
            onClearSearch={() => setLocalSearch('')}
            testId="courses-active-filters"
          />

          {loadingLocal ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="h-36 bg-muted animate-pulse rounded-lg" />
              ))}
            </div>
          ) : filteredLocalCourses.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pagedLocalCourses.map(c => <LocalCourseCard key={c.id} course={c} />)}
            </div>
          ) : (
            <div className="text-center py-16">
              <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No courses found</h3>
              <p className="text-muted-foreground mb-4">
                {localSearch ? 'Try a different search term.' : 'Browse the Pulse Catalog to add courses.'}
              </p>
            </div>
          )}
          <TablePagination page={localPage} total={filteredLocalCourses.length} onPageChange={setLocalPage} testId="courses-pagination" itemLabel="courses" />
        </TabsContent>

        {/* ── Pulse Catalog Tab ── */}
        <TabsContent value="pulse" className="space-y-4 mt-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search Pulse catalog..."
                value={pulseSearchInput}
                onChange={(e) => handlePulseSearchInput(e.target.value)}
                className="pl-10"
                data-testid="search-pulse"
              />
            </div>
            {availableLevels.length > 0 && (
              <Select value={levelFilter} onValueChange={handleLevelFilter}>
                <SelectTrigger className="w-40" data-testid="filter-level">
                  <SelectValue placeholder="All Levels" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Levels</SelectItem>
                  {availableLevels.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {availableTechs.length > 0 && (
              <Select value={techFilter} onValueChange={handleTechFilter}>
                <SelectTrigger className="w-48" data-testid="filter-technology">
                  <SelectValue placeholder="All Technologies" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Technologies</SelectItem>
                  {availableTechs.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Button variant="outline" size="icon" onClick={handleRefreshPulse} disabled={loadingPulse}
              title="Refresh catalog" data-testid="refresh-pulse-btn">
              <RefreshCw className={`w-4 h-4 ${loadingPulse ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {/* Cache info */}
          {pulseLastUpdated && (
            <p className="text-xs text-muted-foreground">
              Catalog last updated: {format(pulseLastUpdated, 'MMM d, yyyy h:mm a')}
              {pulseTotal > 0 && ` · ${pulseTotal} courses`}
            </p>
          )}

          {/* Error state */}
          {pulseError && (
            <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-800">Could not load Pulse catalog</p>
                <p className="text-sm text-red-700 mt-1">{pulseError}</p>
                <Button size="sm" variant="outline" className="mt-3" onClick={() => loadPulseCatalog(1, pulseSearch, levelFilter, techFilter)}>
                  Try Again
                </Button>
              </div>
            </div>
          )}

          {/* Loading state */}
          {loadingPulse && !pulseError && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="h-40 bg-muted animate-pulse rounded-lg" />
              ))}
            </div>
          )}

          {/* Courses grid */}
          {!loadingPulse && !pulseError && pulseCourses.length > 0 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="pulse-catalog-grid">
                {pulseCourses.map((course, idx) => (
                  <PulseCourseCard
                    key={course.pulse_id || idx}
                    course={course}
                    onAdd={handleAddFromPulse}
                    isAdded={addedCourseIds.has(course.pulse_id)}
                    adding={addingCourseId === course.pulse_id}
                  />
                ))}
              </div>
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-3 pt-4">
                  <Button variant="outline" size="sm" disabled={pulsePage <= 1}
                    onClick={() => loadPulseCatalog(pulsePage - 1, pulseSearch, levelFilter, techFilter)}>
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {pulsePage} of {totalPages}
                  </span>
                  <Button variant="outline" size="sm" disabled={pulsePage >= totalPages}
                    onClick={() => loadPulseCatalog(pulsePage + 1, pulseSearch, levelFilter, techFilter)}>
                    Next
                  </Button>
                </div>
              )}
            </>
          )}

          {/* Empty state */}
          {!loadingPulse && !pulseError && pulseCourses.length === 0 && (
            <div className="text-center py-16">
              <GraduationCap className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">
                {pulseSearch || levelFilter !== 'all' || techFilter !== 'all'
                  ? 'No courses match your filters'
                  : 'Pulse catalog not loaded yet'}
              </h3>
              <p className="text-muted-foreground mb-4">
                {pulseSearch || levelFilter !== 'all' || techFilter !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'Click the refresh button to load courses from Pulse'}
              </p>
              {!pulseSearch && levelFilter === 'all' && techFilter === 'all' && (
                <Button onClick={() => loadPulseCatalog(1)} className="bg-lime text-lime-foreground">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Load Pulse Catalog
                </Button>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CoursesPage;
